import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import axios from 'axios';
import { API_BASE } from '../config';

export default function Upload() {
  const [file, setFile]           = useState(null);
  const [dragging, setDragging]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [error, setError]         = useState('');
  const [pipeline, setPipeline]   = useState('premium');
  const [enableSplat, setEnableSplat] = useState(true);
  const [fps, setFps]             = useState(5);
  const { user } = useAuth();
  const inputRef = useRef();
  const navigate = useNavigate();

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    const validExts = ['mp4','mov','avi','m4v','3gp'];
    if (!validExts.includes(ext) && !f.type.startsWith('video/')) {
      setError('Please upload a video file (.mp4, .mov, .avi)');
      return;
    }
    if (f.size > 2 * 1024 * 1024 * 1024) {
      setError('File must be under 2GB');
      return;
    }
    setError('');
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const fmt = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes/1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes/1024/1024).toFixed(1)} MB`;
    return `${(bytes/1024/1024/1024).toFixed(2)} GB`;
  };

  const etaLabel = () => {
    if (pipeline === 'basic') return '~30 min';
    return enableSplat ? '~90 min' : '~10 min';
  };

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true); setError(''); setProgress(0);
    try {
      const token = user ? await user.getIdToken() : 'stub';
      const headers = { 'Authorization': `Bearer ${token}`, 'x-api-key': 'test-key-123' };

      const { data } = await axios.post(
        `${API_BASE}/jobs?filename=${encodeURIComponent(file.name)}`,
        null, { headers }
      );
      const { job_id, upload_url } = data;

      await axios.put(upload_url, file, {
        headers: { 'Content-Type': file.type || 'video/quicktime' },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / e.total) * 100)),
      });

      await axios.post(`${API_BASE}/jobs/${job_id}/start`, {
        pipeline_type: pipeline,
        enable_splat:  pipeline === 'premium' ? enableSplat : false,
        fps,
      }, { headers });

      navigate(`/jobs/${job_id}`);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || err.message || 'Upload failed — please try again');
      setUploading(false);
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '52px 32px' }}>
      <div style={{ marginBottom: 36 }}>
        <div style={{
          fontFamily: 'IBM Plex Mono, monospace', fontSize: 10,
          color: 'var(--rust)', letterSpacing: 3,
          textTransform: 'uppercase', marginBottom: 14, opacity: 0.9,
        }}>New Scan</div>
        <h1 style={{ fontSize: 30, fontWeight: 500, marginBottom: 10 }}>
          Upload walkthrough video
        </h1>
        <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.7 }}>
          Walk slowly around the space. civil3d will reconstruct it in 3D,
          label surfaces, and measure everything automatically.
        </p>
      </div>

      {/* Pipeline toggle */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 16,
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: 4,
      }}>
        {[
          { id: 'premium', label: 'Premium', desc: 'Measurements + DXF + AI' },
          { id: 'basic',   label: 'Basic',   desc: 'Quick 3D preview'        },
        ].map(p => (
          <button key={p.id} onClick={() => setPipeline(p.id)}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 6,
              border: pipeline === p.id ? '1px solid var(--rust)' : '1px solid transparent',
              background: pipeline === p.id ? 'rgba(232,84,84,0.08)' : 'transparent',
              cursor: 'pointer', textAlign: 'left',
            }}>
            <div style={{
              fontFamily: 'IBM Plex Mono, monospace', fontSize: 11,
              color: pipeline === p.id ? 'var(--rust)' : 'var(--muted)',
              fontWeight: 500, marginBottom: 2,
            }}>{p.label}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{p.desc}</div>
          </button>
        ))}
      </div>

      {/* Advanced options: FPS + Splat toggle */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '14px 16px', marginBottom: 20,
        display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontFamily: 'IBM Plex Mono, monospace', fontSize: 10,
            color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1,
          }}>FPS</span>
          {[3, 5, 10].map(f => (
            <button key={f} onClick={() => setFps(f)}
              style={{
                padding: '4px 10px', borderRadius: 4, fontSize: 12,
                fontFamily: 'IBM Plex Mono, monospace',
                border: fps === f ? '1px solid var(--rust)' : '1px solid var(--border)',
                background: fps === f ? 'rgba(232,84,84,0.08)' : 'transparent',
                color: fps === f ? 'var(--rust)' : 'var(--muted)',
                cursor: 'pointer',
              }}>{f}</button>
          ))}
        </div>

        {pipeline === 'premium' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <span style={{
              fontFamily: 'IBM Plex Mono, monospace', fontSize: 10,
              color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1,
            }}>Gaussian Splat</span>
            <button
              onClick={() => setEnableSplat(v => !v)}
              style={{
                width: 40, height: 22, borderRadius: 11,
                background: enableSplat ? 'var(--rust)' : 'var(--border)',
                border: 'none', cursor: 'pointer', position: 'relative',
                transition: 'background 0.2s',
              }}
            >
              <div style={{
                width: 16, height: 16, borderRadius: '50%', background: 'white',
                position: 'absolute', top: 3,
                left: enableSplat ? 21 : 3,
                transition: 'left 0.2s',
              }} />
            </button>
            <span style={{
              fontFamily: 'IBM Plex Mono, monospace', fontSize: 10,
              color: enableSplat ? 'var(--rust)' : 'var(--muted)',
            }}>{enableSplat ? 'ON' : 'OFF'}</span>
          </div>
        )}
      </div>

      {/* Drop zone */}
      <div
        onClick={() => !uploading && inputRef.current.click()}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        style={{
          border: `1px dashed ${dragging ? 'var(--rust)' : file ? 'var(--green)' : 'var(--border-hi)'}`,
          borderRadius: 8, padding: '40px 32px', textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          background: dragging ? 'var(--rust-soft)' : 'var(--surface)',
          transition: 'all 0.15s', marginBottom: 20,
        }}
      >
        <input ref={inputRef} type="file"
          accept=".mp4,.mov,.avi,.m4v,.3gp,video/*"
          style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files[0])}
        />
        {file ? (
          <>
            <div style={{
              width: 40, height: 40, borderRadius: 8,
              background: 'rgba(46,204,138,0.1)', border: '1px solid var(--green)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px', fontSize: 18,
            }}>✓</div>
            <div style={{ color: 'var(--white)', fontWeight: 400, marginBottom: 4 }}>{file.name}</div>
            <div style={{ color: 'var(--muted)', fontSize: 12, fontFamily: 'IBM Plex Mono, monospace' }}>
              {fmt(file.size)}
            </div>
            {!uploading && (
              <button onClick={(e) => { e.stopPropagation(); setFile(null); }}
                style={{ marginTop: 12, background: 'none', border: 'none',
                  color: 'var(--muted)', cursor: 'pointer', fontSize: 12 }}>
                Remove
              </button>
            )}
          </>
        ) : (
          <>
            <div style={{
              width: 40, height: 40, borderRadius: 8,
              background: 'var(--elevated)', border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px', fontSize: 18, color: 'var(--muted)',
            }}>↑</div>
            <div style={{ color: 'var(--white)', fontWeight: 400, marginBottom: 6 }}>
              Drop video here or click to browse
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 12, fontFamily: 'IBM Plex Mono, monospace' }}>
              MP4 · MOV · AVI · Max 2GB
            </div>
          </>
        )}
      </div>

      {/* Upload progress */}
      {uploading && (
        <div style={{ marginBottom: 20 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', marginBottom: 8,
            fontSize: 12, fontFamily: 'IBM Plex Mono, monospace', color: 'var(--muted)',
          }}>
            <span>{progress < 100 ? 'Uploading...' : 'Starting pipeline...'}</span>
            <span>{progress}%</span>
          </div>
          <div style={{ height: 2, background: 'var(--faint)', borderRadius: 2 }}>
            <div style={{
              height: '100%', width: `${progress}%`,
              background: 'var(--rust)', borderRadius: 2, transition: 'width 0.3s',
            }} />
          </div>
        </div>
      )}

      {error && (
        <div style={{
          background: 'rgba(232,84,84,0.08)', border: '1px solid rgba(232,84,84,0.25)',
          borderRadius: 6, padding: '11px 14px', color: 'var(--red)',
          fontSize: 13, marginBottom: 20,
        }}>{error}</div>
      )}

      {/* Tips */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '18px 20px', marginBottom: 24,
      }}>
        <div style={{
          fontFamily: 'IBM Plex Mono, monospace', fontSize: 9,
          color: 'var(--muted)', letterSpacing: 2,
          textTransform: 'uppercase', marginBottom: 12,
        }}>Scanning guidelines</div>
        {[
          'Move at 0.3m/s — slower means sharper reconstruction',
          'Overlap 30% of paths for complete spatial coverage',
          'Avoid mirrors, glass, and uniform textureless walls',
          '2–5 minutes of footage is optimal for most spaces',
        ].map((tip, i) => (
          <div key={i} style={{
            display: 'flex', gap: 10, marginBottom: 8,
            fontSize: 13, color: 'var(--muted)', lineHeight: 1.5,
          }}>
            <span style={{ color: 'var(--rust)', opacity: 0.7 }}>·</span>
            {tip}
          </div>
        ))}
      </div>

      {/* ETA row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        {[
          [etaLabel(), 'Total pipeline'],
          ['10 files', 'Downloadable outputs'],
          ['Unlimited', 'AI queries'],
        ].map(([v, l]) => (
          <div key={l} style={{
            flex: 1, background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '12px 14px',
          }}>
            <div style={{
              fontFamily: 'IBM Plex Mono, monospace', fontSize: 14,
              fontWeight: 500, color: 'var(--rust)', marginBottom: 4,
            }}>{v}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'IBM Plex Mono, monospace' }}>{l}</div>
          </div>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!file || uploading}
        className="btn btn-primary"
        style={{
          width: '100%', justifyContent: 'center', padding: '13px', fontSize: 13,
          opacity: !file || uploading ? 0.45 : 1,
          cursor: !file || uploading ? 'not-allowed' : 'pointer',
        }}
      >
        {uploading ? 'Processing...' : `Start ${pipeline === 'premium' ? 'Premium' : 'Basic'} Reconstruction →`}
      </button>
    </div>
  );
}
